import { Id, Observer, UserRole } from '@nextforce/api'
import { useCompaniesContext } from 'company/context'
import { isEmpty, isEqual } from 'lodash'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useUserDetails, useUserDetailsPhoneNumber } from './queries/detail'
import { AddEditUserQueryParams } from './queries/shared/types'

export type AddEditUserContextValue = {
  userFields: AddEditUserQueryParams
  setUserFields: React.Dispatch<React.SetStateAction<AddEditUserQueryParams>>
  setUserFieldsByUserDetails: (userDetails: Observer | undefined) => void
  isLoading: boolean
  type: Type
  role: UserRole | undefined
  setType: React.Dispatch<React.SetStateAction<Type>>
  setUserId: React.Dispatch<React.SetStateAction<Id | undefined>>
  setPhoneNumber: React.Dispatch<React.SetStateAction<string | undefined>>
  setRole: React.Dispatch<React.SetStateAction<UserRole | undefined>>
  isIdenticalEditUserFields: () => boolean
  isValid: () => boolean
}

export const AddEditUserContext = createContext({} as AddEditUserContextValue)

interface Props {
  children: React.ReactNode
}

type Type = 'add' | 'editUser' | 'editStores'

export function AddEditUserProvider(props: Props) {
  const [type, setType] = useState<Type>('add')
  const [role, setRole] = useState<UserRole>()
  const { activeCompany } = useCompaniesContext()
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [userId, setUserId] = useState<Id>()
  const addUserQuery = useUserDetailsPhoneNumber(
    phoneNumber,
    type === 'add' && !!phoneNumber
  )
  const userDetailsQuery = useUserDetails(
    activeCompany?.id,
    userId,
    role,
    type === 'editUser' && !!userId
  )

  const defaultUserFields: AddEditUserQueryParams = useMemo(() => {
    let defaultFields: AddEditUserQueryParams = {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      role: role,
      jobTitle: '',
      companyId: activeCompany?.id,
      storeIds: [],
      inviteToNext: false,
    }
    if (type === 'add') {
      defaultFields = {
        ...defaultFields,
        firstName: addUserQuery.data?.firstName,
        lastName: addUserQuery.data?.lastName,
        phoneNumber: phoneNumber,
        storeIds:
          addUserQuery.data?.stores?.map(store => Number(store.id)) ?? [],
        inviteToNext: false,
      }
    } else if (type === 'editUser' || type === 'editStores') {
      defaultFields = {
        ...defaultFields,
        userId: userDetailsQuery.data?.id,
        firstName: userDetailsQuery.data?.firstName,
        lastName: userDetailsQuery.data?.lastName,
        phoneNumber: userDetailsQuery.data?.phoneNumber ?? '',
        jobTitle: userDetailsQuery.data?.jobTitle,
        storeIds:
          userDetailsQuery.data?.stores?.map(store => Number(store.id)) ?? [],
        inviteToNext: false,
      }
    }
    return defaultFields
  }, [
    activeCompany?.id,
    addUserQuery.data,
    userDetailsQuery.data,
    phoneNumber,
    role,
    type,
  ])
  const [userFields, setUserFields] =
    useState<AddEditUserQueryParams>(defaultUserFields)

  const setUserFieldsByUserDetails = useCallback(
    (userDetails: Observer | undefined) => {
      setUserFields({
        ...defaultUserFields,
        userId: userDetails?.id ?? '',
        firstName: userDetails?.firstName ?? '',
        lastName: userDetails?.lastName ?? '',
        phoneNumber: userDetails?.phoneNumber ?? '',
        jobTitle: userDetails?.jobTitle ?? '',
        storeIds: userDetails?.stores?.map(store => Number(store.id)) ?? [],
      })
    },
    [defaultUserFields]
  )

  useEffect(() => {
    setUserFields(defaultUserFields)
  }, [defaultUserFields])

  const isLoading =
    addUserQuery.isLoading || userDetailsQuery.isLoading

  const isIdenticalEditUserFields = useCallback(() => {
    const areStoreIdsEqual =
      userFields.storeIds?.length === defaultUserFields.storeIds?.length &&
      userFields.storeIds?.every(id => defaultUserFields.storeIds?.includes(id))

    if (
      areStoreIdsEqual &&
      isEqual(
        { ...userFields, storeIds: [] },
        { ...defaultUserFields, storeIds: [] }
      )
    ) {
      return true
    } else {
      return false
    }
  }, [defaultUserFields, userFields])

  const isValid = useCallback(() => {
    const valid =
      !isEmpty(userFields.firstName) &&
      !isEmpty(userFields.lastName) &&
      !isEmpty(userFields.phoneNumber) &&
      !isIdenticalEditUserFields()
    if (role === 'observer') {
      return valid && !isEmpty(userFields.storeIds)
    } else if (role === 'guest' || role === 'staff') {
      return valid && !isEmpty(userFields.jobTitle)
    }
    return valid
  }, [
    userFields.firstName,
    userFields.lastName,
    userFields.phoneNumber,
    userFields.storeIds,
    userFields.jobTitle,
    isIdenticalEditUserFields,
    role,
  ])

  return (
    <AddEditUserContext.Provider
      value={{
        userFields: userFields,
        setUserFields,
        setUserFieldsByUserDetails,
        isLoading,
        type,
        setType,
        setPhoneNumber,
        setRole,
        role,
        setUserId,
        isIdenticalEditUserFields: isIdenticalEditUserFields,
        isValid,
      }}
    >
      {props.children}
    </AddEditUserContext.Provider>
  )
}

export function useAddEditUserContext() {
  return useContext<AddEditUserContextValue>(AddEditUserContext)
}
